/**
 * Raising the engagement letter for an enquiry.
 *
 * ===========================================================================
 * THE STEP THAT WAS UNREACHABLE
 * ===========================================================================
 * Every part of this existed except a screen. The API drafts an agreement against a
 * lead, sends it for signature, records execution and countersigns — all built, all
 * tested. Nothing in the staff app called the first of those, so leads, agreements and
 * clients were three lists that never referred to one another, and a fee earner had no
 * way to begin an engagement without using the API directly.
 *
 * ===========================================================================
 * WHY IT DRAFTS RATHER THAN SENDS
 * ===========================================================================
 * Sending FREEZES the text and puts a document in somebody's hands. Recipients are
 * fixed at that point and the wording can no longer change — an amendment means a new
 * version. That is not a thing to do in the same click as writing it.
 *
 * So this creates the draft and hands over to the agreement, where sending is a
 * separate, deliberate act with its own confirmation. One press, one consequence.
 *
 * ===========================================================================
 * PICKING A TEMPLATE VS WRITING FREE TEXT
 * ===========================================================================
 * This used to be one empty textarea with a comment admitting "templates come later".
 * They have: `GET agreements/templates/` returns this firm's active templates, and
 * picking one sends `template: <id>` instead of `body`. The two are mutually exclusive
 * on the backend (`AgreementCreateSerializer.validate`) rather than merged, because
 * `agreements.draft()` renders a template's stored wording exactly as saved — there is
 * no way to start from a template and then edit the text before it becomes the draft.
 * Wanting to adjust the wording for one client is exactly what "write it myself"
 * remains for.
 */

import {
  ConflictError,
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  type Agreement,
  type AgreementTemplate,
  type Lead,
} from '@legalcrm/api';
import {
  Form,
  FormActions,
  FormInput,
  Modal,
  Select,
  Textarea,
  useToast,
  useZodForm,
} from '@legalcrm/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

const schema = z.object({
  title: z.string().min(1, 'Give the letter a title'),
});

type FormValues = z.infer<typeof schema>;

/** What actually reaches the API. Exactly one of `body` / `template`, never both. */
type DraftPayload =
  | { lead: string; title: string; body: string }
  | { lead: string; title: string; template: string };

/** The option value standing for "no template picked" in the `<Select>` below. */
const FREE_TEXT = '';

export function SendEngagementDialog({
  lead,
  open,
  onClose,
}: {
  lead: Lead;
  open: boolean;
  onClose: () => void;
}) {
  const api = useApi();
  const navigate = useNavigate();
  const { toast } = useToast();

  const services = (lead.services ?? []).map((service) => service.name).join(', ');

  const [templateId, setTemplateId] = useState(FREE_TEXT);
  const [body, setBody] = useState('');
  const [bodyError, setBodyError] = useState<string | null>(null);

  // Fetched only while the dialog is open, and not kept warm otherwise — this is a
  // small, complete list read fresh each time somebody starts a letter, not something
  // worth caching across the app.
  const templates = useApiQuery<AgreementTemplate[]>(
    qk.list('agreement-template-options'),
    STAFF.agreementTemplateOptions,
    { enabled: open },
  );
  const available = templates.data ?? [];
  const selected = available.find((template) => template.id === templateId) ?? null;

  const form = useZodForm(schema, {
    values: {
      // Named after the work, not after the enquiry. This title appears on the client's
      // own copy and in their portal, so "Engagement letter — Immigration" reads as
      // correspondence from a solicitor where "Lead L-00042" reads as a database row.
      title: services ? `Engagement letter — ${services}` : 'Engagement letter',
    },
  });

  const draft = useApiMutation<Agreement, DraftPayload>(
    {
      mutationFn: (payload) => api.post<Agreement>(`${STAFF.agreements}/`, payload),
      // The conversion plan looks for an agreement on this lead, so it is stale the
      // moment one exists — and it is what decides whether Convert is available.
      invalidates: [qk.all('agreements'), qk.all('lead-conversion-plan'), qk.all('leads')],
    },
    {
      onSuccess: (agreement) => {
        toast({
          title: 'Draft created',
          description: 'Add the signatories and send it when the wording is right.',
          tone: 'success',
        });
        onClose();
        navigate(`/agreements/${agreement.id}`);
      },
      onError: (error) =>
        toast({
          title: 'The draft was refused',
          description:
            error instanceof ConflictError ? error.detail : 'Try again.',
          tone: 'warning',
        }),
    },
  );

  const handleSubmit = (values: FormValues) => {
    if (selected) {
      setBodyError(null);
      return draft.mutateAsync({ lead: lead.id, title: values.title, template: selected.id });
    }
    if (!body.trim()) {
      setBodyError('The letter needs its terms.');
      return;
    }
    setBodyError(null);
    return draft.mutateAsync({ lead: lead.id, title: values.title, body });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Engagement letter"
      description={`For ${lead.full_name}. This creates a draft — nothing is sent until you send it.`}
    >
      <Form form={form} ariaLabel="Engagement letter" error={draft.error} onSubmit={handleSubmit}>
        <FormInput form={form} name="title" label="Title" autoComplete="off" />

        <Select
          label="Start from"
          value={templateId}
          onChange={(event) => setTemplateId(event.currentTarget.value)}
          options={[
            { value: FREE_TEXT, label: 'Write it myself' },
            ...available.map((template) => ({ value: template.id, label: template.name })),
          ]}
          hint={
            available.length === 0 && !templates.isLoading
              ? 'No active templates yet — a firm administrator can add one under Settings > Agreement templates.'
              : 'A saved template is used exactly as written. To change the wording for this client, write it yourself instead.'
          }
        />

        {selected ? (
          // Read-only: there is nowhere for an edit here to go. `draft()` copies the
          // template's stored body, not whatever might be typed into a box like this.
          <div>
            <p className="mb-1 text-sm text-ink">Wording (from "{selected.name}")</p>
            <div className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-md border border-line bg-surface-sunken p-3 text-sm text-ink-muted">
              {selected.body}
            </div>
          </div>
        ) : (
          <Textarea
            label="Terms"
            rows={14}
            value={body}
            onChange={(event) => {
              setBody(event.currentTarget.value);
              if (bodyError) setBodyError(null);
            }}
            error={bodyError ?? undefined}
            hint="Paste your standard engagement wording. Once it is sent the text is frozen — an amendment means a new version, so it is worth reading before it goes."
          />
        )}

        {services && (
          <p className="text-sm text-ink-muted">
            On conversion this enquiry opens: <span className="text-ink">{services}</span>
          </p>
        )}

        <FormActions
          submitLabel="Create the draft"
          busy={draft.isPending}
          onCancel={onClose}
        />
      </Form>
    </Modal>
  );
}
